import { useState } from "react";
import { useExitIntent } from "@/hooks/useExitIntent";
import { handleFormSubmit } from "@/utils/handleFormSubmit";
import { isValidEmail } from "@/utils/validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Mail } from "lucide-react";

export function ExitIntentPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("School");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useExitIntent(() => {
    const dismissed = sessionStorage.getItem("exit_intent_dismissed");
    if (!dismissed) setIsOpen(true);
  });

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem("exit_intent_dismissed", "true");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!isValidEmail(email)) {
      setError("Please enter a valid email");
      return;
    }
    setIsSubmitting(true);
    await handleFormSubmit({ email, userType }, "exit_intent_popup");
    setIsSubmitting(false);
    setSubmitted(true);
    setTimeout(() => setIsOpen(false), 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative animate-slide-up">
        <button onClick={handleClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        {!submitted ? (
          <>
            <div className="w-12 h-12 bg-[#e85a1f]/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-[#e85a1f]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Wait!</h3>
            <p className="text-gray-600 mb-6">Get our free Academic Branding Calendar before you go.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full"
                  required
                />
              </div>
              <div className="flex gap-2">
                {["School", "Corporate"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setUserType(type)}
                    className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors ${
                      userType === type
                        ? "bg-[#e85a1f] text-white border-[#e85a1f]"
                        : "bg-white text-gray-700 border-gray-200 hover:border-[#e85a1f]"
                    }`}
                  >
                    I am a: {type}
                  </button>
                ))}
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <Button type="submit" disabled={isSubmitting} className="w-full bg-[#e85a1f] hover:bg-[#d14d15] text-white font-semibold">
                {isSubmitting ? "Sending..." : "Send Me The Calendar"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">You are on the list!</h3>
            <p className="text-gray-600">Check your email for the Academic Branding Calendar.</p>
          </div>
        )}
      </div>
    </div>
  );
}
